import * as THREE from 'three';
import {ApiCallInteraction} from "../interactions/external_call";
import {DialogueGoInteraction} from "../interactions/dialogue_go";
import {HideObjectMulti} from "../interactions/hide_object_multi";
import {ShowObject} from "../interactions/show_object";
import {ShowMessageHandler} from "../interactions/show_message_handler";
import {DefaultInteraction} from "../interactions/interaction";
import {HideObject} from "../interactions/hide_object";
import {RotateObject} from "../interactions/rotate_object";
import {SwapObject} from "../interactions/swap_object";
import {TeleportMetaversePrompt} from "../interactions/teleport_metaverse";
import {JoinMeetingInteraction} from "../interactions/join_meeting";
import {JoinMeetingPrompt} from "../interactions/join_meeting_prompt";
import {ObjectAnimation} from "../interactions/object_animation";

export class InteractionHandler {

	defaultHandler = new DefaultInteraction() //Stub to allow us access to member methods

	mouseAbsolute = new THREE.Vector2();
	mouse = new THREE.Vector2();
	lastMouse = new THREE.Vector2();
	raycaster = new THREE.Raycaster();

	camera = null;
	scene = null;
	player = null;

	handlers = [];

	placeholders = {
		"$wallet_addr": "",
	}

	collisionHandler

	tickDelay = 1000;
	tickInterval = null;

	lastHover = null;
	lastProximitySensor = null
	activeEvents = {}

	//Context array of managers available to interaction manager
	ctx = {}

	interactables = []; //Array of obj items that can be interacted with in the scene
	interactions = {}; //Keyed by alphaID of the interactable object returning the available interactions

	startX = 0;
	startY = 0;

	constructor(domElement, scene, camera, ctx) {
		this.scene = scene
		this.camera = camera
		this.ctx = ctx
		this.collisionHandler = this.ctx.collision

		this.raycaster.near = 0.2; //Min interaction distance (stops from clipping issues)
		this.raycaster.far = 100; //Max interaction distance

		this._registerEventListeners(domElement)

		this.registerHandlers()

		this.tickInterval = setInterval(() => {
			this.tick(this.tickDelay)
		}, this.tickDelay)
	}

	registerHandlers() {
		this.addInteractionHandler(new DialogueGoInteraction())
		this.addInteractionHandler(new ApiCallInteraction())
		this.addInteractionHandler(new ObjectAnimation({scene: this.scene, handler: this.ctx.animation}))
		this.addInteractionHandler(new HideObjectMulti({scene: this.scene}))
		this.addInteractionHandler(new ShowObject({scene: this.scene}))
		this.addInteractionHandler(new HideObject({scene: this.scene}))
		this.addInteractionHandler(new ShowMessageHandler({camera: this.camera}))
		this.addInteractionHandler(new RotateObject({scene: this.scene}))
		this.addInteractionHandler(new SwapObject({scene: this.scene}))
		this.addInteractionHandler(new TeleportMetaversePrompt())
		this.addInteractionHandler(new JoinMeetingPrompt())
		this.addInteractionHandler(new JoinMeetingInteraction({rtcManager: this.ctx.rtc}))
	}

	_registerEventListeners(domElement) {
		window.addEventListener('interaction-trigger', (e) => this.onInteractionFire(e))
		domElement.addEventListener('pointermove', (e) => this.onMouseMove(e));
		domElement.addEventListener('mousedown', (e) => this.onMouseDown(e));
		domElement.addEventListener('mouseup', (e) => this.onMouseUp(e));
	}

	onMouseDown(e) {
		this.startX = e.pageX;
		this.startY = e.pageY;
	}

	onMouseUp(e) {
		const delta = 6; //Sensitivity of the detections
		const diffX = Math.abs(e.pageX - this.startX);
		const diffY = Math.abs(e.pageY - this.startY);

		if (diffX < delta && diffY < delta) {
			this.onMouseClick(e)
		}
	}

	setPlaceholder(key, value) {
		this.placeholders[key] = value
	}

	_addInteractable(obj, interaction, simplifyMesh = true) {
		// console.log("Adding interactable", interaction.action_type, interaction)
		if (this.handlers[interaction.action_type])
			this.handlers[interaction.action_type].onAdd(obj, interaction) //Trigger any add requirements on handler

		if (!simplifyMesh) {
			obj.userData.bbox = new THREE.Box3().setFromObject(obj);
			return;
		}

		this.collisionHandler.mergeGeometriesAsync(this.collisionHandler.buildMesh(obj, {}, {})).then(mg => {
			if (!mg) //Unable to merge geometries
				return

			mg.userData.isTerrain = obj.userData.isTerrain;
			mg.userData.alphaID = obj.userData.alphaID;
			mg.userData.bbox = new THREE.Box3().setFromObject(obj); //Add bounding dimensions
		})
	}

	addInteractable(obj, interactions, simplifyMesh = true) {
		if (!obj.userData.alphaID) {
			console.error('Object must have an alphaID')
			return
		}

		if (this.interactions[obj.userData.alphaID]) {
			console.error("interactions for object already registered", {
				alphaID: obj.userData.alphaID, newObj: obj, existingObj: this.interactions[obj.userData.alphaID]
			})
			return;
		}

		const iTypes = Object.keys(interactions)
		let added = 0
		for (let i = 0; i < iTypes.length; i++) {
			const iTyp = iTypes[i]
			if (!interactions[iTyp].action_type)
				continue

			this._addInteractable(obj, interactions[iTyp], simplifyMesh)
			added++
		}

		if (added === 0)
			return; //Nothing added

		this.interactables.push(obj)
		this.interactions[obj.userData.alphaID] = interactions
	}

	removeInteractable(alphaID) {
		let index = this.interactables.findIndex(obj => obj.userData.alphaID === alphaID)
		if (index === -1) {
			console.error('Object not found')
			return
		}
		this.interactables.splice(index, 1)
		delete this.interactions[alphaID]
	}

	update(delta, player) {
		if (player) {
			this.player = player
			// if (this.lastProximitySensor && this.lastProximitySensor.interaction) {
			// 	const {interactable, interaction} = this.lastProximitySensor
			//
			// 	if (!interaction.proximity)
			// 		return;
			//
			// 	if (this.activeEvent && !this.inRange(player, interactable, interaction.proximity.activation_radius)) {
			//
			// 		if (this.handlers[interaction.proximity.action_type])
			// 			this.handlers[interaction.proximity.action_type].onProximityExit(interactable) //Trigger exit event for proximity
			// 		this.activeEvent = false
			// 		this.lastProximitySensor = null
			// 	}
			// }

			//Call onUpdate on our handlers for internal logic updates
			for (let i in this.handlers) {
				this.handlers[i].onUpdate(player, this.interactions)
			}
		}

		if (this.mouse.equals(this.lastMouse))
			return

		this.lastMouse = this.mouse.clone();
		this._raycast()
	}

	tick(delta) {
		if (this.player)
		this._updateInteractableRadius(this.player)
		//Call onUpdate on our handlers for internal logic updates
		for (let i in this.handlers) {
			this.handlers[i].onTick(this.interactions)
		}
	}


	/**
	 * Loops through interactables and triggers teleport event if we are in range
	 * @param player
	 * @private
	 */
	_updateInteractableRadius(player) {

		for (let i = 0; i < this.interactables.length; i++) {
			const interactable = this.interactables[i]
			const interaction = this.interactions[interactable.userData.alphaID]

			if (!interaction.proximity)
				continue

			if (this.inRange(player, interactable, interaction.proximity.activation_radius)) {
				// console.debug("player in range of", interaction, interactable)


//				if (this.handlers[interaction.proximity.action_type] && !this.activeEvents[interactable.userData.alphaID])
//					this.handlers[interaction.proximity.action_type].onProximityEnter(interaction.proximity, interactable) //Trigger enter logic

				switch(interaction.proximity.action_type) {
					case 'teleport_to_metaverse':
								this._emitTeleportEvent(interaction.proximity.data.metaverse_addr)
								break
					case 'show_message':
								this.cards[interactable.userData.alphaID].visible = true
								this.visibleCards.push(interactable)
								break
					case 'teleport_character_to':
								const [x, y, z] = interaction.proximity.data.position
								interactable.position.set(x, y, z)
								break
				}


				this.lastProximitySensor = {interactable, interaction}
				this.activeEvents[interactable.userData.alphaID] = true
			} else if(this.activeEvents[interactable.userData.alphaID]) {
				delete this.activeEvents[interactable.userData.alphaID]
				if (this.handlers[interaction.proximity.action_type])
					this.handlers[interaction.proximity.action_type].onProximityExit(interactable) //Trigger exit event for proximity
			}
		}
	}

	/**
	 * Returns if the player is in range of a given interactables radius
	 * @param player
	 * @param interactable
	 * @param radius
	 * @returns {boolean}
	 */
	inRange(player, interactable, radius = 2) {
		//return this.defaultHandler.inRange(player, interactable, radius)
		const distance = this.euclideanDistance(player, interactable)

		if (distance < radius)
			return true


		return false
		// const d2Box = this._distanceToBox(player.position, interactable.userData.bbox)

		// return (d2Box > -1 && d2Box < radius / 2)
	}

	/**
	 * Calculate the distance between 2 points
	 * @param p1
	 * @param p2
	 * @returns {number}
	 */
	euclideanDistance(p1, p2) {
		const pos1 = p1.position
		const pos2 = p2.position

		return Math.sqrt((pos1.x - pos2.x) * (pos1.x - pos2.x) + (pos1.z - pos2.z) * (pos1.z - pos2.z))
	}

	_emitTeleportEvent(metaverseAddr) {
		window.dispatchEvent(new CustomEvent('teleport', {
			detail: {
				metaverse_addr: metaverseAddr
			}
		}))
	}

	onMouseMove(event) {

		this.mouseAbsolute.x = event.clientX
		this.mouseAbsolute.y = event.clientY

		this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
		this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
	}

	onMouseClick(event) {

		this.mouseAbsolute.x = event.clientX
		this.mouseAbsolute.y = event.clientY

		this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
		this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
		this._raycast(true)
	}

	_raycast(isClick = false) {
		// update the picking ray with the camera and pointer position
		this.raycaster.setFromCamera(this.mouse, this.camera);

		// calculate objects intersecting the picking ray
		// const intersects = this.raycaster.intersectObjects(this.scene.getChildren());
		const intersects = this.raycaster.intersectObjects(this.interactables, isClick);
		if (intersects.length <= 0) {

			//If currently hovering, trigger interaction
			if (this.isHovering()) {
				window.dispatchEvent(new CustomEvent('interaction-out', {
					detail: {
						uuid: this.lastHover,
					}
				}))
			}

			this.clearHover(); //Clear for next loop
			return;
		}

		for (let i = 0; i < intersects.length; i++) {

			//Do for first event
			let baseObj = this.getIntersectionObj(intersects[i])
			if (baseObj === null) {
				continue //No interactable item found
			}

			//Active item if we find a valid item in scene graph
			const visible = baseObj.visible;
			const hasParent = baseObj.type === "Mesh" && baseObj.parent && baseObj.parent.type === "Group";
			const hasAlpha = baseObj.userData.alphaID || (hasParent && baseObj.parent.userData.alphaID);

			if (!hasAlpha || !visible) {
				continue
			}


			let is = baseObj.userData
			if (baseObj.parent && baseObj.parent.type === 'Group') {
				is = Object.assign({}, baseObj.parent.userData, baseObj.userData)
			}

			//If we have a valid interaction - trigger event
			const interaction = this.getInteraction(is.alphaID, isClick ? 'click' : 'hover'); //Get the interaction
			const payload = {
				detail: {
					...is,
					x: this.mouseAbsolute.x,
					y: this.mouseAbsolute.y,
					interaction,
					interactable: this.interactables.find(({ name }) => name === is.alphaID),
				}
			}

			//If we have valid interaction then trigger
			if (interaction !== null) {
				this.onHover(is.alphaID) //Set current hover to this ID for next time

				// if (isClick)
				// 	window.dispatchEvent(new CustomEvent(`interaction-${isClick ? 'click' : 'in'}`, payload))
				// else
				window.dispatchEvent(new CustomEvent(`interaction-trigger`, payload))

				window.dispatchEvent(new CustomEvent(`interaction-${isClick ? 'click' : 'in'}`, payload))
				return;
			}

			if (!this.isHovering() || !this.isHoveringOver(is.alphaID)) {
				this.onHover(is.alphaID)
				return;
			}
			return;
		}

		this.clearHover();
	}

	addInteractionHandler(handler) {
		// console.debug("Adding", handler)
		const key = handler.key()
		if (this.handlers[key]) {
			console.error(`Interaction handler "${key}" already registered`)
			return
		}

		this.handlers[key] = handler
	}

	onInteractionFire(e) {
		// console.log("Interaction fired", e.detail)

		const detail = e.detail
		const interactionData = this._parseInteractionPlaceholders(detail)


		const handler = this.handlers[detail.interaction.action_type]
		if (!handler)
			return

		// console.log("Running interaction handler", handler, detail)

		//Check if we have a blocking property, if so then use that to determine if we block player movement or not
		if (!("blocking" in detail.interaction.data))
			this.collisionHandler.setInteracting(true)
		else
			this.collisionHandler.setInteracting(detail.interaction.data.blocking)

		handler.runInteraction(interactionData, detail.data, (e) => this.onInteractionSuccess(detail.interaction, e), (e) => this.onInteractionError(detail.interaction, e))
	}

	onInteractionSuccess(interaction, data) {
		this.collisionHandler.setInteracting(false)

		// console.log("onInteractionSuccess", interaction, data)

		window.dispatchEvent(new CustomEvent(`interaction-success`, {
			detail: {
				interaction: interaction,
				metadata: data
			}
		}))
	}

	onInteractionError(interaction, e) {
		this.collisionHandler.setInteracting(false)
		window.dispatchEvent(new CustomEvent(`interaction-error`, {
			detail: {
				interaction: interaction,
				metadata: e
			}
		}))
	}

	sendInteractionEvent(event) {
		window.dispatchEvent(new CustomEvent(`interaction-event`, {detail: event}))
	}

	onHover(alphaID) {
		this.lastHover = alphaID;
	}

	clearHover() {
		this.lastHover = null;
	}

	clearTickTimer() {
		clearInterval(this.tickInterval)
		this.tickInterval = null
	}

	isHovering() {
		return this.lastHover !== null
	}

	isHoveringOver(alphaID) {
		return this.lastHover !== alphaID
	}

	getIntersectionObj(intersects) {
		let baseObj = null;
		if (intersects.object.userData.alphaID)
			baseObj = intersects.object
		else {
			intersects.object.traverseAncestors((a) => {
				if (a.userData.alphaID) {
					baseObj = a;
				}
			})
		}
		return baseObj
	}

	getInteraction(alphaID, action) {
		if (!this.interactions[alphaID])
			return null

		const int = this.interactions[alphaID][action]
		if (int === undefined || !int)
			return null

		return int
	}

	_parseInteractionPlaceholders(detail) {
		const data = detail.interaction.data
		const meta = detail.data
		return this._innerParse(data, meta)
	}

	_innerParse(data, meta) {
		if (!data)
			return {}
		// console.log("_innerParse", data, meta)
		const keys = Object.keys(data)
		for (let i = 0; i < keys.length; i++) {
			const v = data[keys[i]];
			if (typeof v === "object") {
				data[keys[i]] = this._innerParse(v, meta)
				continue
			}

			if (typeof v !== "string")
				continue

			if (v.substring(0, 1) !== "$")
				continue

			// console.log("searching for placeholder replacement", v, keys[i])
			if (this.placeholders[v]) //Set placeholder vals first
				data[keys[i]] = this.placeholders[v]

			if (v.indexOf("meta.") > -1) {
				const metaKey = v.replace("$meta.", "")
				const prop = this.getDescendantProp(meta, metaKey)
				if (prop) //Override if present from meta coming with interaction
					data[keys[i]] = prop
			}

		}

		return data
	}

	getDescendantProp = (obj, path) => (
		path.split('.').reduce((acc, part) => acc && acc[part], obj)
	);

	/**
	 * Returns the distance to a given box
	 * @param p1
	 * @param box
	 * @returns {number}
	 * @private
	 */
	_distanceToBox(p1, box) {
		if (!box)
			return -1


		const x = Math.max(box.min.x, Math.min(p1.x, box.max.x));
		const y = Math.max(box.min.y, Math.min(p1.y, box.max.y));
		const z = Math.max(box.min.z, Math.min(p1.z, box.max.z));

		//Return distance
		return Math.sqrt((x - p1.x) * (x - p1.x) +
			(y - p1.y) * (y - p1.y) +
			(z - p1.z) * (z - p1.z))
	}

	reset() {
		this.clearHover()
		this.clearTickTimer();
		this.interactions = {};
		this.interactables = [];
	}

	_intersectSphere(sphere, other) {
		// we are using multiplications because it's faster than calling Math.pow
		const distance = Math.sqrt((sphere.x - other.x) * (sphere.x - other.x) +
			(sphere.y - other.y) * (sphere.y - other.y) +
			(sphere.z - other.z) * (sphere.z - other.z));
		return distance < (sphere.radius + other.radius);
	}
}
